import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Building2, LayoutDashboard, ListTodo, Phone, Users, ClipboardList,
  FolderKanban, BarChart3, Settings, LogOut, Menu, X, Sun, Moon,
  Bell, User, ToggleLeft, ToggleRight, RefreshCw, Globe, BookOpen, ConciergeBell, Eye, Package, FileText, Megaphone
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { registerServiceWorker, subscribeToPush, playNotificationSound, isIos, isStandalone } from '../lib/push';
import { IosInstallBanner } from './IOSInstallBanner';
import { AndroidBatteryBanner } from './AndroidBatteryBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Layout = ({ children }) => {
  const { user, logout, toggleShift, isAdmin, token } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const seenIdsRef = useRef(null);

  const handleRefresh = () => {
    window.location.reload();
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 20000);
    // Register service worker + subscribe to push notifications with auth token
    // iOS: Only attempt push subscription if running as standalone (Home Screen) PWA
    registerServiceWorker().then(() => {
      if (token) {
        // Skip push subscription on iOS Safari (non-standalone) - it will always fail
        if (isIos() && !isStandalone()) {
          console.log('[Push] iOS Safari detected - push requires Home Screen installation');
          return;
        }
        subscribeToPush(token).then(result => {
          if (result.ok) {
            console.log('[Push] Successfully subscribed to push notifications');
          } else {
            console.log('[Push] Subscription result:', result.reason);
          }
        });
      }
    });
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      const data = response.data;
      // Detect newly arrived unread notifications -> play sound + toast
      const currentIds = new Set(data.map(n => n.id));
      if (seenIdsRef.current === null) {
        seenIdsRef.current = currentIds;
      } else {
        const newUnread = data.filter(n => !n.read && !seenIdsRef.current.has(n.id));
        if (newUnread.length > 0) {
          playNotificationSound();
          const latest = newUnread[0];
          toast(latest.title, {
            description: latest.message,
            duration: 9000,
            className: 'border-l-4 border-l-accent font-medium',
          });
        }
        seenIdsRef.current = currentIds;
      }
      setNotifications(data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  // Fetch active modules for sidebar filtering
  const [activeModules, setActiveModules] = useState(null);
  
  useEffect(() => {
    const fetchActiveModules = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/modules/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setActiveModules(response.data);
      } catch (error) {
        console.error('Failed to fetch active modules:', error);
        // Fallback: show all modules if API fails
        setActiveModules(null);
      }
    };
    fetchActiveModules();
  }, [token]);

  const allNavItems = [
    { path: '/', icon: LayoutDashboard, label: 'dashboard' },
    { path: '/tasks', icon: ListTodo, label: 'tasks' },
    { path: '/courtesy-calls', icon: Phone, label: 'courtesyCalls' },
    { path: '/guest-relations', icon: Users, label: 'guestRelations' },
    { path: '/checklists', icon: ClipboardList, label: 'checklists' },
    { path: '/projects', icon: FolderKanban, label: 'projects' },
    { path: '/sops', icon: BookOpen, label: 'sops' },
    { path: '/handover', icon: FileText, label: 'handover' },
    { path: '/announcements', icon: Megaphone, label: 'announcements' },
    { path: '/guest-admin', icon: ConciergeBell, label: 'guest' },
    { path: '/lost-found', icon: Package, label: 'lostFound' },
    { path: '/ai-visibility', icon: Eye, label: 'aiVisibility' },
    { path: '/reports', icon: BarChart3, label: 'reports' },
    { path: '/admin', icon: Settings, label: 'admin' },
  ];

  // Filter nav items based on: 1) active modules from API, 2) user's menu_access, 3) admin role
  const navItems = allNavItems.filter(item => {
    // Dashboard is always visible
    if (item.label === 'dashboard') return true;
    
    // Check if module is globally/org enabled (from API)
    if (activeModules && !activeModules.includes(item.label)) return false;
    
    // Check user's explicit menu_access restriction (staff only)
    if (!isAdmin && Array.isArray(user?.menu_access) && !user.menu_access.includes(item.label)) return false;
    
    return true;
  });

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 sm:p-6 border-b border-border">
            <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="logo">
              <img src="/flowupy-logo.png" alt="FlowUpy" className="w-9 h-9 sm:w-11 sm:h-11 object-contain" />
              <span className="text-lg sm:text-xl font-bold">FlowUpy</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                data-testid={`nav-${item.label}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{t(item.label)}</span>
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-3 sm:p-4 border-t border-border">
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate text-sm sm:text-base">{user?.name}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{user?.role === 'admin' ? (language === 'tr' ? 'Yönetici' : 'Admin') : (language === 'tr' ? 'Personel' : 'Staff')}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 min-w-0 overflow-x-hidden">
        {/* Header */}
        <header className="sticky top-0 z-30 glass-effect">
          <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 gap-1">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-muted transition-colors flex-shrink-0"
              data-testid="menu-toggle"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <div className="flex-1 min-w-0" />

            <div className="flex items-center gap-1 sm:gap-2">
              {/* Shift Toggle - Easy Access */}
              {user?.role === 'staff' && (
                <button
                  onClick={toggleShift}
                  className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-colors ${
                    user?.on_shift 
                      ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30' 
                      : 'bg-muted text-muted-foreground border border-border'
                  }`}
                  data-testid="header-shift-toggle"
                >
                  {user?.on_shift ? <ToggleRight className="w-4 h-4 sm:w-5 sm:h-5" /> : <ToggleLeft className="w-4 h-4 sm:w-5 sm:h-5" />}
                  <span className="hidden sm:inline">{user?.on_shift ? t('onShift') : t('offShift')}</span>
                </button>
              )}

              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 hover:bg-muted transition-colors relative"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 bg-destructive text-[10px] font-bold flex items-center justify-center text-white rounded-full animate-pulse ring-2 ring-background">
                      {unreadCount}
                    </span>
                  )}
                </button>
                {showNotifications && (
                  <div className="fixed left-2 right-2 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 bg-card border border-border rounded-lg shadow-lg animate-slide-up z-50" data-testid="notifications-dropdown">
                    <div className="flex items-center justify-between p-3 border-b border-border">
                      <span className="font-medium text-sm">{t('notifications')}</span>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-primary hover:underline">
                          {t('markAllRead')}
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="p-4 text-center text-muted-foreground text-sm">{t('noNotifications')}</p>
                      ) : (
                        notifications.slice(0, 10).map(notif => (
                          <button
                            key={notif.id}
                            onClick={() => markRead(notif.id)}
                            className={`w-full text-left p-3 border-b border-border last:border-0 hover:bg-muted transition-colors ${!notif.read ? 'bg-primary/5' : ''}`}
                            data-testid={`notification-item-${notif.id}`}
                          >
                            <p className="font-medium text-sm">{notif.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{notif.message}</p>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 hover:bg-muted transition-colors"
                data-testid="header-theme-toggle"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="p-2 hover:bg-muted transition-colors flex items-center gap-1"
                data-testid="header-language-toggle"
                title="Dil / Language"
              >
                <Globe className="w-5 h-5" />
                <span className="text-xs sm:text-sm font-medium">{language.toUpperCase()}</span>
              </button>

              {/* Refresh */}
              <button
                onClick={handleRefresh}
                className="p-2 hover:bg-muted transition-colors"
                data-testid="header-refresh-btn"
                title={language === 'en' ? 'Refresh' : 'Yenile'}
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-3 sm:p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Platform-specific onboarding banners */}
      <IosInstallBanner />
      <AndroidBatteryBanner />
    </div>
  );
};
